import {useState} from "react";
import type {Rule} from "antd/es/form";
import {Field, FieldColSize, FieldDataTypes} from "./Field";
import {Button, Checkbox, Flex, Form, Input, InputNumber, Popconfirm, Select, theme} from "antd";
import {generateRandomString} from "../../../shared/utils";
import {DeleteOutlined, RightCircleOutlined} from "@ant-design/icons";
import { MethodOptions } from "../../../models/automation";
import { useTranslate } from "@refinedev/core";
import { CalculatorOptions } from "../../../shared/constants/calculatorOptions";

const DEFAULT_FIELD_COL_SIZE: FieldColSize = {
    xs: 24,
    sm: 12,
    md: 8,
    lg: 8,
    xl: 8,
};

const SPECIAL_SELECT_FIELD_NAMES = new Set(["calculator", "method"]);

const normalizeColSize = (colSize?: Partial<FieldColSize>): FieldColSize => {
    return {
        ...DEFAULT_FIELD_COL_SIZE,
        ...(colSize ?? {}),
    };
};

const parseOptionalPositiveInt = (value: unknown): number | undefined => {
    const num = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(num) || num <= 0) {
        return undefined;
    }
    return Math.floor(num);
};

const isStringTextInputField = (field: InputField): boolean => {
    return (
        field.dataType === FieldDataTypes.STRING &&
        !SPECIAL_SELECT_FIELD_NAMES.has(field.name)
    );
};

export const buildInputFieldRules = (
    field: InputField,
    translate: (key: string, options?: Record<string, unknown>) => string,
): Rule[] => {
    const rules: Rule[] = [];
    if (field.required) {
        rules.push({ required: true });
    }
    if (!isStringTextInputField(field)) {
        return rules;
    }
    if (field.minChars !== undefined && field.minChars > 0) {
        rules.push({
            min: field.minChars,
            message: translate("fields.errors.minLength", { min: field.minChars }),
        });
    }
    if (field.maxChars !== undefined && field.maxChars > 0) {
        rules.push({
            max: field.maxChars,
            message: translate("fields.errors.maxLength", { max: field.maxChars }),
        });
    }
    return rules;
};

const InputFieldRenderer: React.FC<{ field: InputField }> = ({ field }) => {
    const { token } = theme.useToken();
    const translate = useTranslate();

    return (
        <div className={field.hidden ? 'hidden' : undefined}>
            <Form.Item
                label={<Flex gap={8}><RightCircleOutlined style={{ color: token.colorPrimary }} />{field.label}</Flex>}
                rules={buildInputFieldRules(field, translate)}
                name={field.name}
            >
                {field.name === "calculator" ? (
                    <Select
                        placeholder="Select a option and change input text above"
                        allowClear
                    >
                        {CalculatorOptions.map((opt) => (
                            <Select.Option key={opt} value={opt}>
                                {opt}
                            </Select.Option>
                        ))}
                    </Select>
                ) : field.name === "method" ? (
                    <Select allowClear options={MethodOptions} />
                ) : field.dataType === FieldDataTypes.NUMBER ? (
                    <InputNumber className="w-full" />
                ) : (
                    <Input />
                )}
            </Form.Item>
        </div>
    );
};

export class InputField implements Field {
    id = generateRandomString(10);
    name = 'InputField' + generateRandomString(4);
    description = 'The basic input field';
    label = 'Input';
    dataType = FieldDataTypes.STRING;
    required = false;
    defaultValue = '';
    hidden = false;
    minChars?: number;
    maxChars?: number;
    colSize: FieldColSize = DEFAULT_FIELD_COL_SIZE;
    formTag = 'InputField';
    parent = null;

    constructor(isTemplate = false) {
        if (isTemplate) {
            this.name = 'Input';
        }
    }

    render = () => {
        return <InputFieldRenderer field={this} />;
    };

    renderEditor = (field: Field, selectedComponent, setSelectedComponent, deleteComponent) => {
        return <InputFieldEditor field={field as InputField} deleteComponent={deleteComponent}/>;
    };

    renderPropertiesEditor = (field: Field, updateField: any) => {
        return <InputFieldPropsEditor inputField={field as InputField} setInputField={updateField}/>;
    }

    static fromJSON = (json: any, parent = null) => {
        console.log('InputField from JSON', json);
        const input = new InputField();
        input.id = json.id;
        input.label = json.label;
        input.name = json.name;
        input.description = json.description;
        input.dataType = json.dataType;
        input.required = json.required;
        input.defaultValue = json.defaultValue;
        input.hidden = json.hidden ?? false;
        input.minChars = parseOptionalPositiveInt(json.minChars);
        input.maxChars = parseOptionalPositiveInt(json.maxChars);
        input.colSize = normalizeColSize(json.colSize);
        input.formTag = json.formTag;
        input.parent = parent;
        return input;
    }

    toJSON = () => {
        const json: Record<string, unknown> = {
            id: this.id,
            name: this.name,
            description: this.description,
            label: this.label,
            dataType: this.dataType,
            required: this.required,
            defaultValue: this.defaultValue,
            hidden: this.hidden,
            colSize: this.colSize,
            formTag: this.formTag,
            parent: this.parent?.id,
        };
        if (this.minChars !== undefined) {
            json.minChars = this.minChars;
        }
        if (this.maxChars !== undefined) {
            json.maxChars = this.maxChars;
        }
        return json;
    }

}

export const InputFieldEditor = ({ field, deleteComponent }: { field: InputField; deleteComponent: (f: Field) => void }) => {
    const translate = useTranslate();
    return (
        <Flex justify="space-between">
            <Form.Item
                label={
                    <span>
                        {field.label}
                        {field.hidden && (
                            <span className="ml-2 text-gray-400 text-xs">({translate('forms.fieldHidden')})</span>
                        )}
                    </span>
                }
                name={field.name}
                className="w-full"
                rules={buildInputFieldRules(field, translate)}
            >
                {field.dataType === FieldDataTypes.NUMBER
                    ? <InputNumber required={field.required}/>
                    : <Input required={field.required}/>
                }
            </Form.Item>
            <Popconfirm
                title="Delete the item"
                description="Are you sure to delete this item?"
                onConfirm={() => deleteComponent(field)}
                okText="Yes"
                cancelText="No"
            >
                <Button size='small' danger icon={<DeleteOutlined/>}/>
            </Popconfirm>
        </Flex>
    )
}

export const InputFieldPropsEditor = ({inputField, setInputField}: { inputField: InputField, setInputField: (f: InputField) => void }) => {
    const translate = useTranslate();
    const [required, setRequired] = useState(inputField.required);
    const [hidden, setHidden] = useState(inputField.hidden ?? false);
    const colSize = normalizeColSize(inputField.colSize);
    const isStringType = inputField.dataType === FieldDataTypes.STRING;
    const hasInvalidMinMax =
        isStringType &&
        inputField.minChars !== undefined &&
        inputField.maxChars !== undefined &&
        inputField.minChars > inputField.maxChars;

    const handleInputChange = (prop: keyof InputField) => (e: React.ChangeEvent<HTMLInputElement>) => {
        (inputField as Record<string, unknown>)[prop] = e.target.value;
        setInputField(inputField);
    };

    const handleSelectChange = (prop: keyof InputField) => (value: FieldDataTypes) => {
        (inputField as Record<string, unknown>)[prop] = value;
        if (value !== FieldDataTypes.STRING) {
            inputField.minChars = undefined;
            inputField.maxChars = undefined;
        }
        setInputField(inputField);
    };

    const handleCheckboxChange = (prop: "required" | "hidden") => (e: { target: { checked: boolean } }) => {
        const checked = e.target.checked;
        if (prop === 'required') setRequired(checked);
        if (prop === 'hidden') setHidden(checked);
        inputField[prop] = checked;
        setInputField(inputField);
    };

    const handleColSizeChange = (key: keyof FieldColSize) => (value: number | null) => {
        const nextValue = Math.min(24, Math.max(1, Number(value ?? DEFAULT_FIELD_COL_SIZE[key])));
        inputField.colSize = {
            ...normalizeColSize(inputField.colSize),
            [key]: nextValue,
        };
        setInputField(inputField);
    };

    const handleMinCharsChange = (value: number | null) => {
        inputField.minChars = parseOptionalPositiveInt(value);
        setInputField(inputField);
    };

    const handleMaxCharsChange = (value: number | null) => {
        inputField.maxChars = parseOptionalPositiveInt(value);
        setInputField(inputField);
    };

    return (
        <Form layout="vertical">
            <Form.Item label="Name">
                <Input value={inputField.name} onChange={handleInputChange('name')}/>
            </Form.Item>
            <Form.Item label="Description">
                <Input value={inputField.description} onChange={handleInputChange('description')}/>
            </Form.Item>
            <Form.Item label="Label">
                <Input value={inputField.label} onChange={handleInputChange('label')}/>
            </Form.Item>
            <Form.Item label="Data Type">
                <Select value={inputField.dataType} onChange={handleSelectChange('dataType')}>
                    <Select.Option value={FieldDataTypes.STRING}>String</Select.Option>
                    <Select.Option value={FieldDataTypes.NUMBER}>Number</Select.Option>
                </Select>
            </Form.Item>
            {isStringType && (
                <>
                    <Form.Item label={translate("forms.minChars")}>
                        <InputNumber
                            min={1}
                            max={inputField.maxChars}
                            className="w-full"
                            value={inputField.minChars}
                            onChange={handleMinCharsChange}
                        />
                    </Form.Item>
                    <Form.Item
                        label={translate("forms.maxChars")}
                        validateStatus={hasInvalidMinMax ? "warning" : undefined}
                        help={hasInvalidMinMax ? translate("forms.minMaxCharsInvalid") : undefined}
                    >
                        <InputNumber
                            min={inputField.minChars ?? 1}
                            className="w-full"
                            value={inputField.maxChars}
                            onChange={handleMaxCharsChange}
                        />
                    </Form.Item>
                </>
            )}
            <Form.Item label="Required">
                <Checkbox checked={required} onChange={handleCheckboxChange('required')}/>
            </Form.Item>
            <Form.Item label={translate('forms.fieldHidden')}>
                <Checkbox checked={hidden} onChange={handleCheckboxChange('hidden')}/>
            </Form.Item>
            <Form.Item label="Default Value">
                <Input value={inputField.defaultValue} onChange={handleInputChange('defaultValue')}/>
            </Form.Item>
            <Form.Item label={translate("forms.columnSize")}>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{translate("forms.columnSizeXs")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.xs} onChange={handleColSizeChange("xs")} />
                    </div>
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{translate("forms.columnSizeSm")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.sm} onChange={handleColSizeChange("sm")} />
                    </div>
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{translate("forms.columnSizeMd")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.md} onChange={handleColSizeChange("md")} />
                    </div>
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{translate("forms.columnSizeLg")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.lg} onChange={handleColSizeChange("lg")} />
                    </div>
                    <div>
                        <div className="mb-1 text-xs text-gray-500">{translate("forms.columnSizeXl")}</div>
                        <InputNumber min={1} max={24} className="w-full" value={colSize.xl} onChange={handleColSizeChange("xl")} />
                    </div>
                </div>
            </Form.Item>
        </Form>
    );
};
